// NewsTalk (c) Copyright 2023 by Qwixotic
// 
// File:   Article.js
// Author: Percipient Reverence
// Date:   8-May-2023
//
// Description:
//
//   Display the comments about an article.  Will have at least two endpoints: ArticleMIDEP that
//   finds an article by the MID of a comment within it; and ArticleAIDEP, which will find an
//   article by the AID.

// React imports
import React, { useEffect, useState }         from 'react'
import        { useParams, useOutletContext } from 'react-router-dom'

// MUI imports
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import { DataGrid, GridRowsProp, GridColDef, gridClasses, gridExpandedSortedRowIdsSelector,
         useGridApiRef } from '@mui/x-data-grid';
import { grey } from '@mui/material/colors';

// Local imports
import { ArticleHeader, CommentItem,
         ReplyButtonAndPaginationToolbar, ReplyButtonAndPaginationFooter } from './lib/Components'
import { ConfigVars, gotoComment, myFetch } from './lib/Util'
import './NewsTalk.css'

// Load article thread based on MID of a comment within the thread
export const ArticleMIDEP = () => {
  console.log('ArticleMIDEP')

  const {signinCredentials} = useOutletContext()
  // Create some state
  let [Article, ArticleSet] = useState([])

  const Params = useParams()

  // Fetch SID and MID, but if they weren't set then we use default values of 1, which may or may
  // not be consistent but only when the page providing the link has bad values, so
  // we're not going to worry about mismatched SID/MID problems.

  let SID = Params.SID ? parseInt(Params.SID) : 1
  if (SID <= 1) {
    SID = 1;
  }


  let MID = Params.MID ? parseInt(Params.MID) : 1
  if (MID <= 1) {
    MID = 1;
  }

  useEffect(() => {
    myFetch('/nt/Article/MID/' + SID + '/' + MID)
      .then(res => res.json())
      .then(ArticleSet)
  }, [SID, MID])

  return ArticleDisplay(signinCredentials, Article, null, MID)
}
  
// Load article thread based on its AID, then position at the specified page
export const ArticleAIDEP = () => {
  console.log('ArticleAIDEP')

  const {signinCredentials} = useOutletContext()
  // Create some state
  let [Article, ArticleSet] = useState([])

  const Params = useParams()

  // Fetch SID and MID, but if they weren't set then we get default values of 1, which may or may
  // not be consistent.  This can only happen when the page providing the link has bad values, so
  // we're not going to worry about mismatched SID/MID problems.

  let SID = Params.SID ? parseInt(Params.SID) : 1
  if (SID <= 1) {
    SID = 1;
  }

  let AID = Params.AID ? parseInt(Params.AID) : 1
  if (AID <= 1) {
    AID = 1;
  }

  let pageNum = Params.pageNum ? parseInt(Params.pageNum) : 1
  if (pageNum < 1) {
    pageNum = 1;
  }

  useEffect(() => {
    myFetch('/nt/Article/AID/' + SID + '/' + AID)
      .then((res) => res.json())
      .then(ArticleSet)
  }, [SID, AID, pageNum])

  return ArticleDisplay(signinCredentials, Article, pageNum)
}

const ArticleDisplay = (signinCredentials, Article, pageNum=1, rowID=0) => {

  const apiRef = useGridApiRef()

  const getRowSpacing = React.useCallback((params) => {
    return {
      top: params.isFirstVisible ? 0 : 10,
      bottom: params.isLastVisible ? 0 : 10
    }
  }, []);

  let retVal
  if (!Article || !Article.Section || !Article.Comments || !Article.MediaOutlet) {
    retVal = (
      <>
        Loading...
      </>
    )
  } else {
    // Add replies and replyTos information.  First create an object with references to all the
    // comments by MID.
    let Comments = {}
    for (const Comment of Article.Comments) {
      Comment.SID = Article.Section.SID
      Comments[Comment.MID] = Comment
      Comments[Comment.MID] = Comment
    }

    // Now cycle through all the comments creating references from the ReplyToMID field
    for (const Comment of Article.Comments) {
      if (Comment.ReplyToMID) {
        if (!Comments[Comment.ReplyToMID].ReplyMIDs) {
          Comments[Comment.ReplyToMID].ReplyMIDs = {}
        }
        Comments[Comment.ReplyToMID].ReplyMIDs[Comment.MID] = 1
      }
    }

    // Build the rows and columns of the datagrid
    const postingAllowed = signinCredentials.idnum>0 && !signinCredentials.acode
    let Rows: GridRowsProp, Columns: GridColDef[]
    if (Article.Comments.length) {
      const CommentsObject = {}
      Rows = Article.Comments.map((Comment) => {
        // Add references to each comment so they're easy to look up later
        CommentsObject[Comment.MID] = Comment
        return (
          {id:            Comment.MID,
           Comment:       Comment
          })})

      // The examples claim you can add the "hide: true" property to a column to hide it, but that does
      // not appear to work.  You have to instead use the columnVisibilityModel property with
      // <DataGrid>.
      Columns = [
        {field: 'id',
         sortComparator: (v1, v2) => v1-v2, sortingOrder: ['asc', 'desc', null]},
        {field: 'Comment', headerName: Article.Name, flex: 100,
         renderCell: (params) => (<CommentItem Comment={params.value} Comments={CommentsObject} apiRef={apiRef}
                                               TotalMsgs={Article.Comments.length} PostingAllowed={postingAllowed}/>)}
      ]
    } else {
      Rows = [
        {id:      0,
         Comment: <Box sx={{width: '100%', marginTop: '50px', marginBottom: '50px', textAlign: 'center', fontSize: 20}}>
                    Be the first to comment
                  </Box>
        }
      ]
      Columns = [
        {field: 'id'},
        {field: 'Comment', headerName: Article.Name, flex: 100,
         renderCell: (params) => (params.value)}
      ]
    }

    console.log('Expr: ' + JSON.stringify(signinCredentials))
    let slots = {
      columnHeaders: React.forwardRef(() => null),
      toolbar: () => <ReplyButtonAndPaginationToolbar SID={Article.Section.SID} AID={Article.AID}
                                                      PostCount={Article.PostCount} PostingAllowed={postingAllowed}/>
    }
    let hideFooter
    if (Article.PostCount > 0) {
      slots.pagination = () => <ReplyButtonAndPaginationFooter SID={Article.Section.SID} AID={Article.AID}
                                                               PostCount={Article.PostCount} PostingAllowed={postingAllowed}/>
      hideFooter = false
    } else {
      hideFooter = true
    }

    const pagination = pageNum
          ? {paginationModel: {pageSize: ConfigVars.MessagesPerPage, page: pageNum-1}}
          : {paginationModel: {pageSize: ConfigVars.MessagesPerPage}}

    retVal = (
      <>
        <ArticleHeader Article={Article}/>
        <Button onClick={() => apiRef.current.sortColumn({field: 'id', sortable: true}, 'asc', false)}>Sort Asc</Button>
        <Button onClick={() => apiRef.current.sortColumn({field: 'id', sortable: true}, 'desc', false)}>Sort Desc</Button>
        <DataGrid
          apiRef={apiRef} initialState={{pagination: pagination}}
          rows={Rows} columns={Columns} autoHeight={true}
          hideFooter={hideFooter} hideFooterSelectedRowCount={true}
          getRowSpacing={getRowSpacing} rowSpacingType='margin'
          sx={{[`.${gridClasses.row}`]: {
            bgcolor: grey[200]
          }
              }}
          getRowHeight={() => {return 'auto'}}
          slots={slots}
          columnVisibilityModel={{id: false}}
        />
      </>
    )
  }
        
  useEffect(() => {
    /*
    // ```` It looks like I was trying to do something with sortorder, but I'm not sure what.  I
    // have to go, so leave this here for now and I'll figure out if I need to do something for sort
    // order some other time.
    if (apiRef.current.sortOrder) {
      console.log('apiRef.current.sortOrder Type: ' + typeof(apiRef.current.sortOrder))
    } else {
      console.log('apiRef.current.sortOrder is not defined')
    }
    */

    if (rowID) {
      // ```` The longer the thread the longer this timeout must be because it has to wait for the
      // datagrid to finish loading.  When the datagrid has many pages of 15 posts each then the
      // timeout has to be very long.  And probably slower servers will require more time.  What I
      // need is a way to detect when the datagrid has finished loading, but I think I recall
      // searching for a way to do that before and not having any luck.  This is bad news that the
      // time required isn't constant.  I'll have to put time into this, but not right now.
      gotoComment(rowID, apiRef, 200)
    }
  }, [apiRef, rowID])

  return retVal
}
